import {
  useMemo,
} from 'react';
import {
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import isNil from 'lodash/isNil';
import {
  ProviderEntryConfigDTO,
  ProviderSuggestionDTO,
} from 'dtos';
import {
  ProviderSuggestionContext,
} from 'dtos/providerSuggestionContext';
import {
  postApiSettingsUpsertProviderEntryConfig,
} from 'endpoints';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import {
  useClaimReferringProviderSuggestions as useClaimReferringProviderSuggestionsQuery,
  useEncounterReferringProviderSuggestions,
} from 'pages/Dashboard/hooks/providers';
import {
  getProviderEntryConfigQueryOptions,
} from 'pages/Dashboard/components/ReferringProvider/providerEntryConfig';
import {
  ProviderEntryConfigTarget,
} from 'pages/Dashboard/components/ReferringProvider/providerEntryConfigTarget';

export type AppointmentReferringProviderSuggestion = {
  id: string;
  label: string;
  subtitle?: string | null;
  badge: string;
  npi?: string | null;
  state?: string | null;
  providerEntry: NonNullable<ProviderSuggestionDTO['providerEntry']>;
};

export type AppointmentReferringProviderSuggestionsResult = {
  suggestions: AppointmentReferringProviderSuggestion[];
  isLoading: boolean;
  error: Error | null;
};

function getProviderLabel(
  providerEntry: ProviderSuggestionDTO['providerEntry'],
  fallback: string,
) {
  return getDisplayName(providerEntry?.formattedNames, fallback);
}

function getProviderSubtitle(providerEntry: ProviderSuggestionDTO['providerEntry']) {
  return providerEntry?.npi?.number
    ? `NPI ${providerEntry.npi.number}`
    : null;
}

function getContextLabel(context: ProviderSuggestionContext | undefined) {
  switch (context) {
    case ProviderSuggestionContext.PCP:
      return 'PCP';
    case ProviderSuggestionContext.PreviousVisit:
      return 'SALT';
    case ProviderSuggestionContext.FromAppointment:
      return 'Appointment';
    case ProviderSuggestionContext.PreviousClaim:
      return 'SALT';
    default:
      return 'Suggested';
  }
}

function buildSuggestion(
  suggestion: ProviderSuggestionDTO,
): AppointmentReferringProviderSuggestion | null {
  const { providerEntry, context } = suggestion;
  if (isNil(providerEntry)) {
    return null;
  }

  return {
    id: `${context ?? 'suggested'}-${providerEntry.providerEntryId ?? providerEntry.npi?.number ?? providerEntry.formattedNames?.fullName ?? ''}`,
    badge: getContextLabel(context),
    label: getProviderLabel(providerEntry, 'Suggested Provider'),
    subtitle: getProviderSubtitle(providerEntry),
    npi: providerEntry.npi?.number ?? null,
    state: providerEntry.state ?? null,
    providerEntry,
  };
}

function buildReferringProviderSuggestions(suggestions: ProviderSuggestionDTO[] | undefined) {
  return (suggestions ?? []).flatMap((suggestion) => {
    const result = buildSuggestion(suggestion);
    return isNil(result) ? [] : [result];
  });
}

export function useGetProviderEntryConfig(target: ProviderEntryConfigTarget | null, isEnabled = true) {
  const options = getProviderEntryConfigQueryOptions(target);
  return useQuery({ ...options, enabled: isEnabled && options.enabled });
}

export function useUpsertProviderEntryConfig() {
  return useMutation<ProviderEntryConfigDTO, Error, ProviderEntryConfigDTO>({
    mutationFn: (payload) => postApiSettingsUpsertProviderEntryConfig(payload),
  });
}

export function useClaimReferringProviderSuggestions(
  claimId: number | null | undefined,
): AppointmentReferringProviderSuggestionsResult {
  const {
    data,
    error = null,
    isFetching: isLoading,
  } = useClaimReferringProviderSuggestionsQuery(
    claimId ?? 0,
    !isNil(claimId),
  );

  const suggestions = useMemo(() => buildReferringProviderSuggestions(data), [data]);

  return {
    suggestions,
    isLoading,
    error,
  };
}

export function useAppointmentReferringProviderSuggestions(
  patientId: number | null | undefined,
  encounterId: number | null,
): AppointmentReferringProviderSuggestionsResult {
  const {
    data,
    error = null,
    isFetching: isLoading,
  } = useEncounterReferringProviderSuggestions(
    patientId ?? 0,
    encounterId,
    !isNil(patientId),
  );

  const suggestions = useMemo(() => buildReferringProviderSuggestions(data), [data]);

  return {
    suggestions,
    isLoading,
    error,
  };
}
